/**
 * Zustand WebSocket store for the portal SPA.
 *
 * Tracks WebSocket connection lifecycle including status,
 * reconnection attempts, and last message timestamp.
 * Used by the `useWebSocket` hook to manage connection state.
 */

import { create } from 'zustand';

import type { WebSocketState, WebSocketStatus } from '../types/websocket-state';

interface WebSocketActions {
  setStatus: (status: WebSocketStatus) => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;
  setLastMessageAt: (timestamp: string) => void;
}

export type WebSocketStore = WebSocketState & WebSocketActions;

/**
 * Zustand store for WebSocket connection state.
 *
 * Usage:
 * ```ts
 * const { status, reconnectAttempts } = useWebSocketStore();
 * ```
 */
export const useWebSocketStore = create<WebSocketStore>((set) => ({
  status: 'disconnected',
  reconnectAttempts: 0,
  lastMessageAt: null,

  setStatus: (status: WebSocketStatus) => { set({ status }); },

  incrementReconnectAttempts: () =>
    { set((state) => ({ reconnectAttempts: state.reconnectAttempts + 1 })); },

  resetReconnectAttempts: () => { set({ reconnectAttempts: 0 }); },

  setLastMessageAt: (timestamp: string) => { set({ lastMessageAt: timestamp }); },
}));
